import React, { useState, useEffect } from 'react'
import { PDFDownloadLink } from '@react-pdf/renderer'
import { useTranslation } from 'react-i18next'
import { makeApiCall } from '../utils/customerUtils'
import { Calculator, DollarSign, TrendingUp, FileText, Trash2, Copy, Plus, Minus, Download, Save, History, Search, User, Calendar } from 'lucide-react'
import { useLanguage } from '../contexts/LanguageContext'
import { useAuth } from '../hooks/useAuth'
import MortgageCalculatorPDF from '../components/MortgageCalculatorPDF'
import SaveScenariosModal from '../components/SaveScenariosModal'

interface Debt {
  id: string
  name: string
  amount: number
  interestRate: number
  includeInMortgage: boolean
}

interface MortgageScenario {
  id: string
  name: string
  // Scenario Info
  bankName: string
  comments: string
  // Core Financial Calculator Fields (any 3 of 4)
  mortgageAmount: number
  interestRate: number
  amortizationYears: number
  amortizationMonths: number
  monthlyPayment: number
  paymentFrequency: string
  // Additional Property & Cost Fields
  propertyValue: number
  downPayment: number
  termYears: number
  termMonths: number
  insured: boolean
  insuranceRate: number
  notaryFees: number
  cashback: number
  penalty: number
  debts: Debt[]
  // Calculated Results
  totalCostOverTerm?: number
  totalInterestOverTerm?: number
  totalCostOverAmortization?: number
  totalInterestOverAmortization?: number
  totalPayments?: number
  debtInterestCostOverTerm?: number
  consolidatedDebtAmount?: number
  savingsFromConsolidation?: number
}

interface PaymentFrequency {
  id: number
  name: string
  periods_per_year: number
  is_accelerated: boolean
  position: number
  is_active: boolean
}

interface Bank {
  id: number
  name: string
  position: number
  is_active: boolean
}

const MortgageCalculator: React.FC = () => {
  const { language } = useLanguage()
  const { user } = useAuth()
  const { t } = useTranslation()
  
  // Pre-populate with common Canadian payment frequencies
  const defaultPaymentFrequencies: PaymentFrequency[] = [
    { id: 1, name: 'Monthly', periods_per_year: 12, is_accelerated: false, position: 1, is_active: true },
    { id: 2, name: 'Bi-weekly', periods_per_year: 26, is_accelerated: false, position: 2, is_active: true },
    { id: 3, name: 'Accelerated Bi-weekly', periods_per_year: 26, is_accelerated: true, position: 3, is_active: true },
    { id: 4, name: 'Weekly', periods_per_year: 52, is_accelerated: false, position: 4, is_active: true },
    { id: 5, name: 'Accelerated Weekly', periods_per_year: 52, is_accelerated: true, position: 5, is_active: true }
  ]

  const [scenarios, setScenarios] = useState<MortgageScenario[]>([
    createEmptyScenario('1', t('mortgageCalculator.scenario1')),
    createEmptyScenario('2', t('mortgageCalculator.scenario2')),
    createEmptyScenario('3', t('mortgageCalculator.scenario3'))
  ])
  const [paymentFrequencies, setPaymentFrequencies] = useState<PaymentFrequency[]>(defaultPaymentFrequencies)
  const [banks, setBanks] = useState<Bank[]>([])
  const [loading, setLoading] = useState(true)
  const [activeScenarios, setActiveScenarios] = useState<string[]>(['1', '2'])
  const [customerName, setCustomerName] = useState('')
  const [propertyAddress, setPropertyAddress] = useState('')
  const [showSaveModal, setShowSaveModal] = useState(false)
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'success' | 'error'>('idle')
  const [showSavedScenarios, setShowSavedScenarios] = useState(false)
  const [savedScenarios, setSavedScenarios] = useState<any[]>([])
  const [selectedCustomerForHistory, setSelectedCustomerForHistory] = useState<any>(null)
  const [customers, setCustomers] = useState<any[]>([])
  const [searchCustomerTerm, setSearchCustomerTerm] = useState('')

  function createEmptyScenario(id: string, name: string): MortgageScenario {
    return {
      id,
      name,
      // Scenario Info
      bankName: '',
      comments: '',
      // Core Financial Calculator Fields - user enters 3, we calculate the 4th
      mortgageAmount: 320000,
      interestRate: 5.25,
      amortizationYears: 25,
      amortizationMonths: 0,
      monthlyPayment: 0, // This will be calculated
      paymentFrequency: 'Monthly',
      // Additional Fields
      propertyValue: 400000,
      downPayment: 80000,
      termYears: 5,
      termMonths: 0,
      insured: false,
      insuranceRate: 2.8,
      notaryFees: 2500,
      cashback: 0,
      penalty: 0,
      debts: []
    }
  }

  // Core Financial Calculator Functions
  const calculateMissingField = (scenario: MortgageScenario): MortgageScenario => {
    const { mortgageAmount, interestRate, amortizationYears, amortizationMonths, monthlyPayment, paymentFrequency } = scenario
    
    // Get payment frequency details
    const freqData = paymentFrequencies.find(f => f.name === paymentFrequency)
    const paymentsPerYear = freqData?.periods_per_year || 12
    const totalAmortizationYears = amortizationYears + (amortizationMonths / 12)
    const totalPayments = Math.ceil(totalAmortizationYears * paymentsPerYear)
    const periodRate = (interestRate / 100) / paymentsPerYear
    
    // Count non-zero fields to determine what to calculate
    const nonZeroFields = [
      mortgageAmount > 0 ? 'amount' : null,
      interestRate > 0 ? 'rate' : null,
      totalPayments > 0 ? 'amortization' : null,
      monthlyPayment > 0 ? 'payment' : null
    ].filter(Boolean)
    
    if (nonZeroFields.length !== 3) {
      // Need exactly 3 fields to calculate the 4th
      return scenario
    }
    
    let updatedScenario = { ...scenario }
    
    // Calculate missing payment
    if (!nonZeroFields.includes('payment')) {
      const payment = calculatePayment(mortgageAmount, periodRate, totalPayments)
      updatedScenario.monthlyPayment = Math.round(payment * 100) / 100
    }
    
    // Calculate missing principal/amount
    else if (!nonZeroFields.includes('amount')) {
      const principal = calculatePrincipal(monthlyPayment, periodRate, totalPayments)
      updatedScenario.mortgageAmount = Math.round(principal)
    }
    
    // Calculate missing interest rate
    else if (!nonZeroFields.includes('rate')) {
      const annualRate = calculateInterestRate(mortgageAmount, monthlyPayment, totalPayments, paymentsPerYear)
      updatedScenario.interestRate = Math.round(annualRate * 100) / 100
    }
    
    // Calculate missing amortization
    else if (!nonZeroFields.includes('amortization')) {
      const payments = calculateAmortizationPayments(mortgageAmount, monthlyPayment, periodRate)
      const years = Math.floor(payments / paymentsPerYear)
      const months = Math.round((payments / paymentsPerYear - years) * 12)
      updatedScenario.amortizationYears = years
      updatedScenario.amortizationMonths = months
    }
    
    return updatedScenario
  }
  
  const calculatePayment = (principal: number, periodRate: number, numPayments: number): number => {
    if (periodRate === 0) return principal / numPayments
    return principal * (periodRate * Math.pow(1 + periodRate, numPayments)) / (Math.pow(1 + periodRate, numPayments) - 1)
  }
  
  const calculatePrincipal = (payment: number, periodRate: number, numPayments: number): number => {
    if (periodRate === 0) return payment * numPayments
    return payment * (Math.pow(1 + periodRate, numPayments) - 1) / (periodRate * Math.pow(1 + periodRate, numPayments))
  }
  
  const calculateInterestRate = (principal: number, payment: number, totalPayments: number, paymentsPerYear: number): number => {
    // Newton-Raphson method to solve for interest rate
    let rate = 0.05 // Initial guess: 5%
    let iteration = 0
    const maxIterations = 100
    const tolerance = 0.0001
    
    while (iteration < maxIterations) {
      const periodRate = rate / paymentsPerYear
      const calculatedPayment = calculatePayment(principal, periodRate, totalPayments)
      const error = calculatedPayment - payment
      
      if (Math.abs(error) < tolerance) break
      
      // Calculate derivative for Newton-Raphson
      const delta = 0.0001
      const periodRateDelta = (rate + delta) / paymentsPerYear
      const paymentDelta = calculatePayment(principal, periodRateDelta, totalPayments)
      const derivative = (paymentDelta - calculatedPayment) / delta
      
      rate = rate - error / derivative
      iteration++
    }
    
    return Math.max(0, rate * 100) // Convert to percentage
  }
  
  const calculateAmortizationPayments = (principal: number, payment: number, periodRate: number): number => {
    if (periodRate === 0) return principal / payment
    if (payment <= principal * periodRate) return Infinity // Payment too small
    return -Math.log(1 - (principal * periodRate) / payment) / Math.log(1 + periodRate)
  }

  useEffect(() => {
    const fetchConfiguration = async () => {
      try {
        const response = await makeApiCall('api/scenarios/config')
        if (response.ok) {
          const config = await response.json()
          // Merge API frequencies with defaults, preferring API data if available
          const apiFrequencies = config.paymentFrequencies || []
          if (apiFrequencies.length > 0) {
            setPaymentFrequencies(apiFrequencies)
          }
          setBanks(config.banks || [])
        }
      } catch (error) {
        console.error('Error fetching configuration:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchConfiguration()
  }, [])

  // Handle dynamic property calculations when fields change
  const handlePropertyCalculations = (scenarioId: string, field: string, value: number) => {
    setScenarios(prev => prev.map(scenario => {
      if (scenario.id !== scenarioId) return scenario
      
      let updatedScenario = { ...scenario, [field]: value }
      
      // Add consolidated debt amount to mortgage amount
      const consolidatedDebtAmount = updatedScenario.debts
        .filter(debt => debt.includeInMortgage)
        .reduce((sum, debt) => sum + debt.amount, 0)
      
      // Dynamic property/mortgage calculations
      if (field === 'mortgageAmount') {
        // If mortgage amount is manually changed, reset property and down payment to allow fresh start
        updatedScenario.propertyValue = 0
        updatedScenario.downPayment = 0
      } else if (field === 'downPayment' && updatedScenario.mortgageAmount > 0) {
        // If we have mortgage amount and down payment, calculate property value
        updatedScenario.propertyValue = (updatedScenario.mortgageAmount - consolidatedDebtAmount) + updatedScenario.downPayment
      } else if (field === 'propertyValue' && updatedScenario.downPayment >= 0) {
        // If we have property value and down payment, calculate mortgage amount
        updatedScenario.mortgageAmount = (updatedScenario.propertyValue - updatedScenario.downPayment) + consolidatedDebtAmount
      } else if ((field === 'propertyValue' || field === 'downPayment') && updatedScenario.propertyValue > 0 && updatedScenario.downPayment >= 0) {
        // Standard calculation: mortgage = property - down payment + debts
        updatedScenario.mortgageAmount = (updatedScenario.propertyValue - updatedScenario.downPayment) + consolidatedDebtAmount
      }
      
      updatedScenario.consolidatedDebtAmount = consolidatedDebtAmount
      
      return {
        ...updatedScenario,
        ...calculateMortgageDetails(updatedScenario)
      }
    }))
  }

  useEffect(() => {
    // Recalculate all scenarios when debt changes occur
    setScenarios(prev => prev.map(scenario => {
      const consolidatedDebtAmount = scenario.debts
        .filter(debt => debt.includeInMortgage)
        .reduce((sum, debt) => sum + debt.amount, 0)
      
      let updatedScenario = { ...scenario, consolidatedDebtAmount }
      
      // If we have property info, adjust mortgage amount for debt consolidation
      if (scenario.propertyValue > 0 && scenario.downPayment >= 0) {
        updatedScenario.mortgageAmount = (scenario.propertyValue - scenario.downPayment) + consolidatedDebtAmount
      }
      
      return {
        ...updatedScenario,
        ...calculateMortgageDetails(updatedScenario)
      }
    }))
  }, [scenarios.map(s => `${s.debts.map(d => `${d.id}-${d.amount}-${d.interestRate}-${d.includeInMortgage}`).join(',')}`).join('|')])

  const calculateMortgageDetails = (scenario: MortgageScenario) => {
    const { mortgageAmount, interestRate, termYears, termMonths, amortizationYears, amortizationMonths, paymentFrequency, insured, insuranceRate, notaryFees, cashback, penalty, debts } = scenario

    // Validate required fields - need mortgage amount, interest rate, and amortization period
    if (!mortgageAmount || mortgageAmount <= 0 || !interestRate || interestRate <= 0 || (!amortizationYears && !amortizationMonths)) {
      // console.log('Missing required fields:', { mortgageAmount, interestRate, amortizationYears, amortizationMonths })
      return { 
        monthlyPayment: 0, 
        totalCostOverTerm: 0, 
        totalInterestOverTerm: 0,
        totalCostOverAmortization: 0,
        totalInterestOverAmortization: 0,
        totalPayments: 0,
        debtInterestCostOverTerm: 0,
        savingsFromConsolidation: 0
      }
    }

    // Get payment frequency details
    const freqData = paymentFrequencies.find(f => f.name === paymentFrequency)
    const paymentsPerYear = freqData?.periods_per_year || 12
    const isAccelerated = freqData?.is_accelerated || false

    // Add insurance if applicable
    let finalMortgageAmount = mortgageAmount
    if (insured && insuranceRate) {
      const insuranceAmount = mortgageAmount * (insuranceRate / 100)
      finalMortgageAmount = mortgageAmount + insuranceAmount
    }

    // Convert periods to years
    const totalAmortizationYears = amortizationYears + (amortizationMonths / 12)
    const totalTermYears = termYears + (termMonths / 12) || 5 // Default to 5 years if no term set

    // Calculate payment based on full amortization
    const monthlyPayment = calculateMortgagePayment(
      finalMortgageAmount,
      interestRate,
      totalAmortizationYears,
      paymentsPerYear,
      isAccelerated
    )

    // Calculate costs over TERM period (what customer will pay during this term)
    const termPayments = Math.ceil(totalTermYears * paymentsPerYear)
    const totalPaidOverTerm = monthlyPayment * termPayments
    
    // Calculate principal paid down during term using amortization schedule
    const { principalPaid: principalPaidOverTerm } = calculatePrincipalPaid(
      finalMortgageAmount,
      interestRate,
      totalAmortizationYears,
      paymentsPerYear,
      totalTermYears,
      isAccelerated
    )
    
    const totalInterestOverTerm = totalPaidOverTerm - principalPaidOverTerm

    // Calculate cost of debts NOT included in mortgage (separate debt payments over term)
    const separateDebts = debts.filter(debt => !debt.includeInMortgage)
    const debtInterestCostOverTerm = separateDebts.reduce((total, debt) => {
      // Simple interest calculation over term period for debt cost
      const annualInterest = debt.amount * (debt.interestRate / 100)
      const termInterest = annualInterest * totalTermYears
      return total + termInterest
    }, 0)

    // Calculate what the debt interest WOULD cost if consolidated (for savings calculation)
    const consolidatedDebts = debts.filter(debt => debt.includeInMortgage)
    const potentialSeparateDebtCost = consolidatedDebts.reduce((total, debt) => {
      const annualInterest = debt.amount * (debt.interestRate / 100)
      const termInterest = annualInterest * totalTermYears
      return total + termInterest
    }, 0)

    // For consolidated debts, calculate what interest they'll pay at mortgage rate
    const consolidatedDebtInterestAtMortgageRate = consolidatedDebts.reduce((total, debt) => {
      const annualInterest = debt.amount * (interestRate / 100)
      const termInterest = annualInterest * totalTermYears
      return total + termInterest
    }, 0)

    const savingsFromConsolidation = potentialSeparateDebtCost - consolidatedDebtInterestAtMortgageRate

    const totalCostOverTerm = totalInterestOverTerm + debtInterestCostOverTerm + notaryFees + penalty - cashback

    // Calculate costs over full amortization (for reference)
    const amortizationPayments = Math.ceil(totalAmortizationYears * paymentsPerYear)
    const totalPaidOverAmortization = monthlyPayment * amortizationPayments
    const totalInterestOverAmortization = totalPaidOverAmortization - finalMortgageAmount
    const totalCostOverAmortization = totalInterestOverAmortization + notaryFees + penalty - cashback

    return {
      monthlyPayment: Math.round(monthlyPayment * 100) / 100,
      totalCostOverTerm: Math.round(totalCostOverTerm * 100) / 100,
      totalInterestOverTerm: Math.round(totalInterestOverTerm * 100) / 100,
      totalCostOverAmortization: Math.round(totalCostOverAmortization * 100) / 100,
      totalInterestOverAmortization: Math.round(totalInterestOverAmortization * 100) / 100,
      totalPayments: termPayments,
      debtInterestCostOverTerm: Math.round(debtInterestCostOverTerm * 100) / 100,
      savingsFromConsolidation: Math.round(savingsFromConsolidation * 100) / 100
    }
  }

  // Helper function to calculate principal paid over a specific period
  const calculatePrincipalPaid = (
    loanAmount: number,
    annualRate: number,
    amortizationYears: number,
    paymentsPerYear: number,
    termYears: number,
    isAccelerated: boolean
  ) => {
    const semiAnnualRate = annualRate / 100 / 2
    const effectiveRate = Math.pow(1 + semiAnnualRate, 2 / paymentsPerYear) - 1
    
    if (isAccelerated && paymentsPerYear === 26) {
      const monthlyRate = Math.pow(1 + semiAnnualRate, 2 / 12) - 1
      const monthlyPayments = amortizationYears * 12
      const monthlyPayment = (loanAmount * monthlyRate * Math.pow(1 + monthlyRate, monthlyPayments)) / 
                            (Math.pow(1 + monthlyRate, monthlyPayments) - 1)
      const payment = monthlyPayment / 2
      
      let balance = loanAmount
      const termPayments = Math.ceil(termYears * paymentsPerYear)
      
      for (let i = 0; i < termPayments; i++) {
        const interestPayment = balance * effectiveRate
        const principalPayment = payment - interestPayment
        balance -= principalPayment
      }
      
      return { principalPaid: loanAmount - balance }
    }
    
    // Standard calculation
    const totalPayments = amortizationYears * paymentsPerYear
    const payment = (loanAmount * effectiveRate * Math.pow(1 + effectiveRate, totalPayments)) / 
                    (Math.pow(1 + effectiveRate, totalPayments) - 1)
    
    let balance = loanAmount
    const termPayments = Math.ceil(termYears * paymentsPerYear)
    
    for (let i = 0; i < termPayments; i++) {
      const interestPayment = balance * effectiveRate
      const principalPayment = payment - interestPayment
      balance -= principalPayment
    }
    
    return { principalPaid: loanAmount - balance }
  }

  const calculateMortgagePayment = (
    loanAmount: number,
    annualInterestRate: number,
    termYears: number,
    paymentsPerYear: number,
    isAccelerated: boolean = false
  ): number => {
    if (!loanAmount || !annualInterestRate || !termYears || !paymentsPerYear) {
      return 0
    }

    // Semi-annual compounding - convert annual rate to effective rate per payment period
    const semiAnnualRate = annualInterestRate / 100 / 2
    const effectiveRate = Math.pow(1 + semiAnnualRate, 2 / paymentsPerYear) - 1
    
    // For accelerated bi-weekly, use monthly payment calculation then divide by 2
    if (isAccelerated && paymentsPerYear === 26) {
      const monthlyRate = Math.pow(1 + semiAnnualRate, 2 / 12) - 1
      const monthlyPayments = termYears * 12
      const monthlyPayment = (loanAmount * monthlyRate * Math.pow(1 + monthlyRate, monthlyPayments)) / 
                            (Math.pow(1 + monthlyRate, monthlyPayments) - 1)
      return monthlyPayment / 2
    }
    
    // Standard mortgage payment calculation
    const totalPayments = termYears * paymentsPerYear
    
    if (effectiveRate === 0) {
      return loanAmount / totalPayments
    }
    
    const payment = (loanAmount * effectiveRate * Math.pow(1 + effectiveRate, totalPayments)) / 
                    (Math.pow(1 + effectiveRate, totalPayments) - 1)
    
    return payment
  }

  const updateScenario = (id: string, field: keyof MortgageScenario, value: string | number | boolean) => {
    setScenarios(prev => prev.map(s => {
      if (s.id !== id) return s
      
      const updatedScenario = { ...s, [field]: value }
      
      // Only recalculate costs for non-core calculator fields
      const coreFields = ['mortgageAmount', 'interestRate', 'amortizationYears', 'amortizationMonths', 'monthlyPayment']
      
      if (coreFields.includes(field as string)) {
        // For core calculator fields, just update the value without auto-calculating
        return updatedScenario
      } else {
        // For other fields (property, terms, fees, etc), recalculate mortgage details
        return {
          ...updatedScenario,
          ...calculateMortgageDetails(updatedScenario)
        }
      }
    }))
  }
  
  const updateCoreCalculatorField = (id: string, field: keyof MortgageScenario, value: string | number | boolean) => {
    setScenarios(prev => prev.map(s => {
      if (s.id !== id) return s
      
      // Just update the field value without triggering any calculations
      return { ...s, [field]: value }
    }))
  }

  const resetScenario = (id: string) => {
    const scenario = scenarios.find(s => s.id === id)
    if (scenario) {
      const reset = createEmptyScenario(id, scenario.name)
      setScenarios(prev => prev.map(s => s.id === id ? reset : s))
    }
  }

  const duplicateScenario = (fromId: string, toId: string) => {
    const fromScenario = scenarios.find(s => s.id === fromId)
    const toScenario = scenarios.find(s => s.id === toId)
    if (fromScenario && toScenario) {
      const duplicated = { ...fromScenario, id: toId, name: toScenario.name }
      setScenarios(prev => prev.map(s => s.id === toId ? duplicated : s))
    }
  }

  // Debt management functions
  const addDebt = (scenarioId: string) => {
    const newDebt: Debt = {
      id: `debt_${Date.now()}`,
      name: '',
      amount: 0,
      interestRate: 20,
      includeInMortgage: false
    }
    setScenarios(prev => prev.map(s => 
      s.id === scenarioId ? { ...s, debts: [...s.debts, newDebt] } : s
    ))
  }

  const removeDebt = (scenarioId: string, debtId: string) => {
    setScenarios(prev => prev.map(s => 
      s.id === scenarioId ? { ...s, debts: s.debts.filter(d => d.id !== debtId) } : s
    ))
  }

  const updateDebt = (scenarioId: string, debtId: string, field: keyof Debt, value: string | number | boolean) => {
    setScenarios(prev => prev.map(s => 
      s.id === scenarioId ? {
        ...s, 
        debts: s.debts.map(d => d.id === debtId ? { ...d, [field]: value } : d)
      } : s
    ))
  }

  // Handle core financial calculator
  const handleCoreCalculate = (scenarioId: string) => {
    const scenario = scenarios.find(s => s.id === scenarioId)
    if (!scenario) return
    
    const updatedScenario = calculateMissingField(scenario)
    // Also calculate costs after solving for the missing field
    const scenarioWithCosts = {
      ...updatedScenario,
      ...calculateMortgageDetails(updatedScenario)
    }
    setScenarios(prev => prev.map(s => s.id === scenarioId ? scenarioWithCosts : s))
  }

  // Calculate savings compared to best scenario
  const calculateSavings = (currentScenario: MortgageScenario) => {
    const activeScenarioData = scenarios.filter(s => activeScenarios.includes(s.id))
    if (activeScenarioData.length < 2) return null

    const costs = activeScenarioData.map(s => s.totalCostOverTerm || 0)
    const minCost = Math.min(...costs)
    const maxCost = Math.max(...costs)
    const currentCost = currentScenario.totalCostOverTerm || 0
    
    // Only show savings if this is the cheapest option
    return currentCost === minCost ? maxCost - minCost : null
  }

  const handleSaveScenarios = async (customerId: number, title: string) => {
    try {
      setSaveStatus('saving')
      
      // Get active scenarios data
      const activeScenarioData = scenarios.filter(s => activeScenarios.includes(s.id))
      
      const saveData = {
        customerId,
        title,
        scenariosData: activeScenarioData,
        customerInfo: {
          name: customerName,
          propertyAddress: propertyAddress
        }
      }

      const response = await makeApiCall('/api/saved-scenarios', {
        method: 'POST',
        body: JSON.stringify(saveData),
        headers: {
          'Content-Type': 'application/json'
        }
      })
      const data = await response.json()
      
      if (data.savedScenario) {
        setSaveStatus('success')
        setShowSaveModal(false)
        
        // Refresh saved scenarios if customer history is showing
        if (selectedCustomerForHistory && selectedCustomerForHistory.id === customerId) {
          loadCustomerScenarios(customerId)
        }
        
        // Reset save status after delay
        setTimeout(() => setSaveStatus('idle'), 3000)
      }
    } catch (error) {
      console.error('Failed to save scenarios:', error)
      setSaveStatus('error')
      
      // Reset save status after delay
      setTimeout(() => setSaveStatus('idle'), 3000)
    }
  }

  const loadCustomers = async () => {
    try {
      const response = await makeApiCall('/api/customers')
      const data = await response.json()
      if (data.customers) {
        setCustomers(data.customers)
      }
    } catch (error) {
      console.error('Failed to load customers:', error)
    }
  }

  const loadCustomerScenarios = async (customerId: number) => {
    try {
      const response = await makeApiCall(`/api/saved-scenarios/customer/${customerId}`)
      const data = await response.json()
      if (data.savedScenarios) {
        setSavedScenarios(data.savedScenarios)
      }
    } catch (error) {
      console.error('Failed to load saved scenarios:', error)
      setSavedScenarios([])
    }
  }

  const handleSelectCustomerHistory = (customer: any) => {
    setSelectedCustomerForHistory(customer)
    loadCustomerScenarios(customer.id)
  }

  const handleDeleteScenario = async (scenarioId: number) => {
    if (!confirm(t('mortgageCalculator.deleteConfirm'))) {
      return
    }
    try {
      await makeApiCall(`/api/saved-scenarios/${scenarioId}`, { method: 'DELETE' })
      setSavedScenarios(prev => prev.filter(s => s.id !== scenarioId))
    } catch (error) {
      console.error('Failed to delete saved scenario:', error)
    }
  }

  useEffect(() => {
    if (showSavedScenarios && customers.length === 0) {
      loadCustomers()
    }
  }, [showSavedScenarios, customers.length])

  const filteredCustomers = customers.filter(customer =>
    customer.name?.toLowerCase().includes(searchCustomerTerm.toLowerCase()) ||
    customer.email?.toLowerCase().includes(searchCustomerTerm.toLowerCase()) ||
    customer.first_name?.toLowerCase().includes(searchCustomerTerm.toLowerCase()) ||
    customer.last_name?.toLowerCase().includes(searchCustomerTerm.toLowerCase())
  )

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-CA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const toggleScenario = (id: string) => {
    setActiveScenarios(prev => 
      prev.includes(id) 
        ? prev.filter(s => s !== id)
        : [...prev, id]
    )
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)
  }

  const formatPayment = (amount: number) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 2,
    }).format(amount)
  }

  const getComparisonBadge = (scenarios: MortgageScenario[], field: 'monthlyPayment' | 'totalCostOverTerm', scenarioId: string) => {
    const activeScenarioData = scenarios.filter(s => activeScenarios.includes(s.id))
    if (activeScenarioData.length < 2) return null

    const values = activeScenarioData.map(s => s[field] || 0)
    const currentValue = scenarios.find(s => s.id === scenarioId)?.[field] || 0
    
    const minValue = Math.min(...values)
    const maxValue = Math.max(...values)
    
    if (currentValue === minValue && currentValue !== maxValue) {
      return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 ml-2">{t('mortgageCalculator.best')}</span>
    }
    if (currentValue === maxValue && currentValue !== minValue) {
      return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 ml-2">{t('mortgageCalculator.highest')}</span>
    }
    return null
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
        <span className="ml-3 text-gray-600 dark:text-gray-400">{t('mortgageCalculator.loadingCalculator')}</span>
      </div>
    )
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <Calculator className="h-8 w-8 text-gray-900 mr-3" />
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              {t('mortgageCalculator.title')}
            </h1>
          </div>
          
          {/* Save and PDF Export Buttons */}
          <div className="flex items-center space-x-3">
            {activeScenarios.length > 0 && (
              <>
                <button
                  onClick={() => setShowSaveModal(true)}
                  className={`inline-flex items-center px-4 py-2 font-medium rounded-lg transition-colors duration-200 shadow-sm ${
                    saveStatus === 'success' 
                      ? 'bg-green-500 text-white' 
                      : saveStatus === 'error'
                      ? 'bg-red-600 hover:bg-red-700 text-white'
                      : 'bg-green-600 hover:bg-green-700 text-white'
                  }`}
                  disabled={saveStatus === 'saving'}
                >
                  <Save className="h-4 w-4 mr-2" />
                  {saveStatus === 'saving' ? t('mortgageCalculator.saving') : 
                   saveStatus === 'success' ? t('mortgageCalculator.saved') : 
                   saveStatus === 'error' ? t('mortgageCalculator.saveFailed') : 
                   t('mortgageCalculator.saveScenarios')}
                </button>
                <button
                  onClick={() => {
                    console.log('View History clicked, current state:', showSavedScenarios)
                    const newState = !showSavedScenarios
                    console.log('Setting new state to:', newState)
                    setShowSavedScenarios(newState)
                  }}
                  className={`inline-flex items-center px-4 py-2 font-medium rounded-lg transition-colors duration-200 shadow-sm ${
                    showSavedScenarios 
                      ? 'bg-blue-700 hover:bg-blue-800 text-white' 
                      : 'bg-gray-900 hover:bg-gray-800 text-white'
                  }`}
                >
                  <History className="h-4 w-4 mr-2" />
                  {showSavedScenarios ? t('mortgageCalculator.hideHistory') : t('mortgageCalculator.viewHistory')}
                </button>
                <PDFDownloadLink
                document={
                  <MortgageCalculatorPDF
                    scenarios={scenarios}
                    activeScenarios={activeScenarios}
                    language={language}
                    broker={user}
                    customerName={customerName}
                    propertyAddress={propertyAddress}
                  />
                }
                fileName={`mortgage-comparison-${new Date().toISOString().slice(0, 10)}.pdf`}
                className="inline-flex items-center px-4 py-2 bg-gray-900 hover:bg-gray-800 text-white font-medium rounded-lg transition-colors duration-200 shadow-sm"
              >
                {({ blob, url, loading, error }) => (
                  <>
                    <Download className="h-4 w-4 mr-2" />
                    {loading ? t('mortgageCalculator.generating') : t('mortgageCalculator.exportPDF')}
                  </>
                )}
              </PDFDownloadLink>
              </>
            )}
          </div>
        </div>
        <p className="text-gray-600 dark:text-gray-400 text-lg">
          {t('mortgageCalculator.description')}
        </p>

        {/* Saved Scenarios History Panel */}
        {showSavedScenarios && (
          <div className="mt-6 mb-8 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="bg-gradient-to-r from-gray-900 to-blue-700 px-6 py-4">
              <h2 className="text-xl font-semibold text-white flex items-center">
                <History className="h-6 w-6 mr-3" />
                {t('mortgageCalculator.savedScenariosHistory')}
              </h2>
              <p className="text-blue-100 text-sm mt-1">
                {t('mortgageCalculator.browseSaved')}
              </p>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 min-h-96">
              {/* Customer Search Panel */}
              <div className="lg:border-r border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                <div className="p-6">
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-4">{t('mortgageCalculator.selectCustomer')}</h3>
                  
                  {/* Search */}
                  <div className="relative mb-4">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder={t('mortgageCalculator.searchCustomers')}
                      value={searchCustomerTerm}
                      onChange={(e) => setSearchCustomerTerm(e.target.value)}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                    />
                  </div>

                  {/* Customer List */}
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {filteredCustomers.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        {customers.length === 0 ? t('mortgageCalculator.loadingCustomers') : t('mortgageCalculator.noCustomersFound')}
                      </div>
                    ) : (
                      filteredCustomers.map((customer) => (
                        <div
                          key={customer.id}
                          onClick={() => handleSelectCustomerHistory(customer)}
                          className={`flex items-center p-3 rounded-lg cursor-pointer transition-colors ${
                            selectedCustomerForHistory?.id === customer.id
                              ? 'bg-blue-100 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700'
                              : 'hover:bg-gray-100 dark:hover:bg-gray-800 border border-transparent'
                          }`}
                        >
                          <User className="h-4 w-4 text-gray-400 mr-3 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-gray-900 dark:text-white truncate">
                              {customer.name}
                            </div>
                            {customer.email && (
                              <div className="text-sm text-gray-500 dark:text-gray-400 truncate">
                                {customer.email}
                              </div>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>

              {/* Saved Scenarios Panel */}
              <div className="lg:col-span-2 p-6">
                {!selectedCustomerForHistory ? (
                  <div className="text-center py-16">
                    <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                      {t('mortgageCalculator.selectACustomer')}
                    </h3>
                    <p className="text-gray-500 dark:text-gray-400">
                      {t('mortgageCalculator.selectCustomerDesc')}
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                          {t('mortgageCalculator.customerScenarios', { name: selectedCustomerForHistory.name })}
                        </h3>
                        <p className="text-gray-500 dark:text-gray-400">
                          {t('mortgageCalculator.savedScenarioCount', { count: savedScenarios.length })}
                        </p>
                      </div>
                    </div>

                    {savedScenarios.length === 0 ? (
                      <div className="text-center py-12">
                        <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                        <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                          {t('mortgageCalculator.noSavedScenarios')}
                        </h4>
                        <p className="text-gray-500 dark:text-gray-400">
                          {t('mortgageCalculator.noSavedScenariosDesc')}
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {savedScenarios.map((scenario) => (
                          <div
                            key={scenario.id}
                            className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 hover:border-gray-300 dark:hover:border-gray-500 transition-colors bg-white dark:bg-gray-800"
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                                  {scenario.title}
                                </h4>
                                <div className="flex items-center text-sm text-gray-500 dark:text-gray-400 mb-2 space-x-4">
                                  <div className="flex items-center">
                                    <Calendar className="h-4 w-4 mr-1" />
                                    {formatDate(scenario.created_at)}
                                  </div>
                                  <div>
                                    {scenario.scenario_count} scenario{scenario.scenario_count !== 1 ? 's' : ''}
                                  </div>
                                </div>
                                {scenario.customer_info?.name && (
                                  <div className="text-sm text-gray-600 dark:text-gray-300 mb-1">
                                    <strong>{t('mortgageCalculator.customer')}:</strong> {scenario.customer_info.name}
                                  </div>
                                )}
                                {scenario.customer_info?.propertyAddress && (
                                  <div className="text-sm text-gray-600 dark:text-gray-300">
                                    <strong>{t('mortgageCalculator.property')}:</strong> {scenario.customer_info.propertyAddress}
                                  </div>
                                )}
                              </div>

                              <div className="flex items-center space-x-2 ml-4 flex-shrink-0">
                                <PDFDownloadLink
                                  document={
                                    <MortgageCalculatorPDF
                                      scenarios={scenario.scenarios_data}
                                      activeScenarios={scenario.scenarios_data.map((s: any) => s.id)}
                                      language={language}
                                      broker={user}
                                      customerName={scenario.customer_info?.name}
                                      propertyAddress={scenario.customer_info?.propertyAddress}
                                    />
                                  }
                                  fileName={`${scenario.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.pdf`}
                                  className="inline-flex items-center px-3 py-2 bg-gray-900 hover:bg-gray-800 text-white text-sm font-medium rounded-md transition-colors"
                                >
                                  {({ loading }) => (
                                    <>
                                      <Download className="h-4 w-4 mr-1" />
                                      {loading ? t('mortgageCalculator.generating') : t('mortgageCalculator.download')}
                                    </>
                                  )}
                                </PDFDownloadLink>

                                <button
                                  onClick={() => handleDeleteScenario(scenario.id)}
                                  className="inline-flex items-center px-3 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-md transition-colors"
                                  title={t('mortgageCalculator.deleteScenario')}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}
        
        {/* Customer Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('mortgageCalculator.customerName')}
            </label>
            <input
              type="text"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              placeholder={t('mortgageCalculator.customerNamePlaceholder')}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-gray-900 focus:border-gray-900 dark:bg-gray-700 dark:text-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('mortgageCalculator.propertyAddress')}
            </label>
            <input
              type="text"
              value={propertyAddress}
              onChange={(e) => setPropertyAddress(e.target.value)}
              placeholder={t('mortgageCalculator.propertyAddressPlaceholder')}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-gray-900 focus:border-gray-900 dark:bg-gray-700 dark:text-white"
            />
          </div>
        </div>
      </div>

      {/* Scenario Toggle */}
      <div className="mb-6">
        <div className="flex items-center space-x-4">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('mortgageCalculator.activeScenarios')}:</span>
          {scenarios.map(scenario => (
            <label key={scenario.id} className="flex items-center">
              <input
                type="checkbox"
                checked={activeScenarios.includes(scenario.id)}
                onChange={() => toggleScenario(scenario.id)}
                className="mr-2 h-4 w-4 text-gray-900 focus:ring-gray-900 border-gray-300 rounded"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">{scenario.name}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Scenarios Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 mb-8">
        {scenarios.filter(s => activeScenarios.includes(s.id)).map(scenario => (
          <div key={scenario.id} className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <input
                type="text"
                value={scenario.name}
                onChange={(e) => updateScenario(scenario.id, 'name', e.target.value)}
                className="text-xl font-semibold bg-transparent border-none text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 rounded px-2 py-1"
              />
              <div className="flex space-x-2">
                <button
                  onClick={() => duplicateScenario(scenario.id, scenarios.find(s => s.id !== scenario.id && !activeScenarios.includes(s.id))?.id || '3')}
                  className="p-1 text-gray-400 hover:text-gray-900 transition-colors"
                  title="Duplicate scenario"
                >
                  <Copy className="h-4 w-4" />
                </button>
                <button
                  onClick={() => resetScenario(scenario.id)}
                  className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                  title="Reset scenario"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Bank Name and Comments */}
            <div className="mb-6 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  🏦 {t('mortgageCalculator.bankLender')}
                </label>
                <input
                  type="text"
                  value={scenario.bankName}
                  onChange={(e) => updateScenario(scenario.id, 'bankName', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900"
                  placeholder={t('mortgageCalculator.bankPlaceholder')}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  💭 {t('mortgageCalculator.commentsNotes')}
                </label>
                <textarea
                  value={scenario.comments}
                  onChange={(e) => updateScenario(scenario.id, 'comments', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 resize-none"
                  rows={2}
                  placeholder={t('mortgageCalculator.commentsPlaceholder')}
                />
              </div>
            </div>

            {/* Core Financial Calculator */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-semibold text-blue-800 dark:text-blue-200">
                  🧮 {t('mortgageCalculator.coreFinancialCalculator')}
                </h4>
                <button
                  onClick={() => handleCoreCalculate(scenario.id)}
                  className="inline-flex items-center px-3 py-2 bg-gray-900 hover:bg-gray-800 text-white font-medium text-sm rounded-lg transition-colors"
                >
                  <Calculator className="h-4 w-4 mr-2" />
                  {t('mortgageCalculator.calculate')}
                </button>
              </div>
              <p className="text-sm text-gray-900 dark:text-gray-600 mb-4">
                {t('mortgageCalculator.calculatorInstructions')}
              </p>
              
              <div className="grid grid-cols-2 gap-4">
                {/* Mortgage Amount / Principal */}
                <div>
                  <label className="block text-sm font-medium text-blue-700 dark:text-blue-300 mb-1">
                    Mortgage Amount (Principal)
                  </label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="number"
                      value={scenario.mortgageAmount || ''}
                      onChange={(e) => updateCoreCalculatorField(scenario.id, 'mortgageAmount', parseFloat(e.target.value) || 0)}
                      className="w-full pl-10 pr-3 py-2 border border-blue-300 dark:border-gray-900 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900"
                      placeholder="Leave empty to calculate"
                    />
                  </div>
                </div>

                {/* Interest Rate */}
                <div>
                  <label className="block text-sm font-medium text-blue-700 dark:text-blue-300 mb-1">
                    Interest Rate (% per year)
                  </label>
                  <div className="relative">
                    <TrendingUp className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="number"
                      step="0.01"
                      value={scenario.interestRate || ''}
                      onChange={(e) => updateCoreCalculatorField(scenario.id, 'interestRate', parseFloat(e.target.value) || 0)}
                      className="w-full pl-10 pr-3 py-2 border border-blue-300 dark:border-gray-900 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900"
                      placeholder="Leave empty to calculate"
                    />
                  </div>
                </div>

                {/* Amortization Period */}
                <div>
                  <label className="block text-sm font-medium text-blue-700 dark:text-blue-300 mb-1">
                    Amortization Period (Total payoff time)
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="number"
                      min="0"
                      max="50"
                      value={scenario.amortizationYears || ''}
                      onChange={(e) => updateCoreCalculatorField(scenario.id, 'amortizationYears', parseInt(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-blue-300 dark:border-gray-900 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900"
                      placeholder="Years"
                    />
                    <input
                      type="number"
                      min="0"
                      max="11"
                      value={scenario.amortizationMonths || ''}
                      onChange={(e) => updateCoreCalculatorField(scenario.id, 'amortizationMonths', parseInt(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-blue-300 dark:border-gray-900 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900"
                      placeholder="Months"
                    />
                  </div>
                </div>

                {/* Payment Amount */}
                <div>
                  <label className="block text-sm font-medium text-blue-700 dark:text-blue-300 mb-1">
                    Payment Amount
                  </label>
                  <div className="relative mb-2">
                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="number"
                      value={scenario.monthlyPayment || ''}
                      onChange={(e) => updateCoreCalculatorField(scenario.id, 'monthlyPayment', parseFloat(e.target.value) || 0)}
                      className="w-full pl-10 pr-3 py-2 border border-blue-300 dark:border-gray-900 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900"
                      placeholder="Leave empty to calculate"
                    />
                  </div>
                  <select
                    value={scenario.paymentFrequency}
                    onChange={(e) => updateScenario(scenario.id, 'paymentFrequency', e.target.value)}
                    className="w-full px-3 py-2 border border-blue-300 dark:border-gray-900 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 text-sm"
                  >
                    {paymentFrequencies.map(freq => (
                      <option key={freq.id} value={freq.name}>
                        {freq.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Additional Property & Cost Details */}
            <div className="space-y-4">
              {/* Property Value */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('mortgageCalculator.propertyValue')}
                </label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="number"
                    value={scenario.propertyValue || ''}
                    onChange={(e) => handlePropertyCalculations(scenario.id, 'propertyValue', parseFloat(e.target.value) || 0)}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900"
                    placeholder="400,000"
                  />
                </div>
              </div>

              {/* Down Payment */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Down Payment
                </label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="number"
                    value={scenario.downPayment || ''}
                    onChange={(e) => handlePropertyCalculations(scenario.id, 'downPayment', parseFloat(e.target.value) || 0)}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900"
                    placeholder="80,000"
                  />
                </div>
                {scenario.propertyValue > 0 && scenario.downPayment > 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    {((scenario.downPayment / scenario.propertyValue) * 100).toFixed(1)}% down payment
                  </p>
                )}
              </div>

              {/* Mortgage Amount */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Mortgage Amount
                </label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="number"
                    value={scenario.mortgageAmount || ''}
                    onChange={(e) => handlePropertyCalculations(scenario.id, 'mortgageAmount', parseFloat(e.target.value) || 0)}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900"
                    placeholder="Enter directly or calculated from property - down payment"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  💡 Enter directly, or will auto-calculate from property value - down payment
                </p>
              </div>

              {/* Interest Rate */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Interest Rate (%)
                </label>
                <div className="relative">
                  <TrendingUp className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="number"
                    step="0.01"
                    value={scenario.interestRate || ''}
                    onChange={(e) => updateScenario(scenario.id, 'interestRate', parseFloat(e.target.value) || 0)}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900"
                    placeholder="5.25"
                  />
                </div>
              </div>

              {/* Term Period */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <strong>Term Period</strong> (Rate guarantee period)
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <input
                      type="number"
                      min="0"
                      max="10"
                      value={scenario.termYears || ''}
                      onChange={(e) => updateScenario(scenario.id, 'termYears', parseInt(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900"
                      placeholder="5"
                    />
                    <label className="text-xs text-gray-500">Years</label>
                  </div>
                  <div>
                    <input
                      type="number"
                      min="0"
                      max="11"
                      value={scenario.termMonths || ''}
                      onChange={(e) => updateScenario(scenario.id, 'termMonths', parseInt(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900"
                      placeholder="0"
                    />
                    <label className="text-xs text-gray-500">Months</label>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  How long this rate is guaranteed (e.g., 5 years)
                </p>
              </div>

              {/* Amortization Period */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Amortization Period (Total loan length)
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <input
                      type="number"
                      min="0"
                      max="30"
                      value={scenario.amortizationYears || ''}
                      onChange={(e) => updateScenario(scenario.id, 'amortizationYears', parseInt(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900"
                      placeholder="25"
                    />
                    <label className="text-xs text-gray-500">Years</label>
                  </div>
                  <div>
                    <input
                      type="number"
                      min="0"
                      max="11"
                      value={scenario.amortizationMonths || ''}
                      onChange={(e) => updateScenario(scenario.id, 'amortizationMonths', parseInt(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900"
                      placeholder="0"
                    />
                    <label className="text-xs text-gray-500">Months</label>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Total time to pay off the entire mortgage
                </p>
              </div>

              {/* Payment Frequency */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Payment Frequency
                </label>
                <select
                  value={scenario.paymentFrequency}
                  onChange={(e) => updateScenario(scenario.id, 'paymentFrequency', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900"
                >
                  {paymentFrequencies.map(freq => (
                    <option key={freq.id} value={freq.name}>{freq.name}</option>
                  ))}
                </select>
              </div>

              {/* Insurance */}
              <div>
                <label className="flex items-center mb-2">
                  <input
                    type="checkbox"
                    checked={scenario.insured}
                    onChange={(e) => updateScenario(scenario.id, 'insured', e.target.checked)}
                    className="mr-2 h-4 w-4 text-gray-900 focus:ring-gray-900 border-gray-300 rounded"
                  />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">CMHC Insurance</span>
                </label>
                {scenario.insured && (
                  <input
                    type="number"
                    step="0.01"
                    value={scenario.insuranceRate || ''}
                    onChange={(e) => updateScenario(scenario.id, 'insuranceRate', parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900"
                    placeholder="2.8"
                  />
                )}
              </div>

              {/* Notary Fees */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Legal/Notary Fees
                </label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="number"
                    value={scenario.notaryFees || ''}
                    onChange={(e) => updateScenario(scenario.id, 'notaryFees', parseFloat(e.target.value) || 0)}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900"
                    placeholder="2,500"
                  />
                </div>
              </div>

              {/* Cashback */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Cashback/Rebate
                </label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="number"
                    value={scenario.cashback || ''}
                    onChange={(e) => updateScenario(scenario.id, 'cashback', parseFloat(e.target.value) || 0)}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900"
                    placeholder="0"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Enter positive for cashback, negative for additional costs
                </p>
              </div>

              {/* Penalty */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Penalty Fee
                </label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="number"
                    value={scenario.penalty || ''}
                    onChange={(e) => updateScenario(scenario.id, 'penalty', parseFloat(e.target.value) || 0)}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900"
                    placeholder="0"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Prepayment penalty or other fees
                </p>
              </div>

              {/* Debt Consolidation */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    🏦 Existing Debts
                  </label>
                  <button
                    onClick={() => addDebt(scenario.id)}
                    className="flex items-center px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-md hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors"
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Add Debt
                  </button>
                </div>

                {scenario.debts.length === 0 ? (
                  <p className="text-xs text-gray-500 italic">No debts added. Add debts to see consolidation benefits.</p>
                ) : (
                  <div className="space-y-3">
                    {scenario.debts.map(debt => (
                      <div key={debt.id} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <input
                            type="text"
                            value={debt.name}
                            onChange={(e) => updateDebt(scenario.id, debt.id, 'name', e.target.value)}
                            placeholder="Credit Card, Car Loan, etc."
                            className="flex-1 text-sm px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-1 focus:ring-gray-900"
                          />
                          <button
                            onClick={() => removeDebt(scenario.id, debt.id)}
                            className="ml-2 p-1 text-red-500 hover:text-red-700 transition-colors"
                          >
                            <Minus className="h-3 w-3" />
                          </button>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <input
                              type="number"
                              value={debt.amount || ''}
                              onChange={(e) => updateDebt(scenario.id, debt.id, 'amount', parseFloat(e.target.value) || 0)}
                              placeholder="Amount"
                              className="w-full text-sm px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-1 focus:ring-gray-900"
                            />
                            <label className="text-xs text-gray-500">Amount</label>
                          </div>
                          <div>
                            <input
                              type="number"
                              step="0.1"
                              value={debt.interestRate || ''}
                              onChange={(e) => updateDebt(scenario.id, debt.id, 'interestRate', parseFloat(e.target.value) || 0)}
                              placeholder="Rate %"
                              className="w-full text-sm px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-1 focus:ring-gray-900"
                            />
                            <label className="text-xs text-gray-500">Rate %</label>
                          </div>
                        </div>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={debt.includeInMortgage}
                            onChange={(e) => updateDebt(scenario.id, debt.id, 'includeInMortgage', e.target.checked)}
                            className="mr-2 h-3 w-3 text-gray-900 focus:ring-gray-900 border-gray-300 rounded"
                          />
                          <span className="text-xs text-gray-700 dark:text-gray-300">
                            💰 Include in mortgage (consolidate)
                          </span>
                        </label>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Results */}
            <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Results</h4>
              
              <div className="space-y-4">
                {/* Payment */}
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Payment:</span>
                  <div className="flex items-center">
                    <span className="font-semibold text-gray-900 dark:text-white">
                      {formatPayment(scenario.monthlyPayment || 0)}
                    </span>
                    {getComparisonBadge(scenarios, 'monthlyPayment', scenario.id)}
                  </div>
                </div>

                {/* Term Period Results - EMPHASIZED */}
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                  <h5 className="text-sm font-semibold text-blue-800 dark:text-blue-200 mb-2">
                    💰 Cost Over {scenario.termYears}-Year Term
                  </h5>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-blue-700 dark:text-blue-300">Mortgage Interest:</span>
                      <span className="font-semibold text-blue-900 dark:text-blue-100">
                        {formatCurrency(scenario.totalInterestOverTerm || 0)}
                      </span>
                    </div>
                    {scenario.debtInterestCostOverTerm && scenario.debtInterestCostOverTerm > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-blue-700 dark:text-blue-300">Separate Debt Interest:</span>
                        <span className="font-semibold text-blue-900 dark:text-blue-100">
                          {formatCurrency(scenario.debtInterestCostOverTerm)}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-blue-700 dark:text-blue-300">Total Cost:</span>
                      <div className="flex items-center">
                        <span className="font-bold text-lg text-blue-900 dark:text-blue-100">
                          {formatCurrency(scenario.totalCostOverTerm || 0)}
                        </span>
                        {getComparisonBadge(scenarios, 'totalCostOverTerm', scenario.id)}
                      </div>
                    </div>
                    {scenario.savingsFromConsolidation && scenario.savingsFromConsolidation > 0 && (
                      <div className="flex justify-between items-center bg-green-100 dark:bg-green-900/30 rounded px-2 py-1">
                        <span className="text-sm font-medium text-green-700 dark:text-green-300">✅ Consolidation Savings:</span>
                        <span className="font-bold text-green-800 dark:text-green-200">
                          {formatCurrency(scenario.savingsFromConsolidation)}
                        </span>
                      </div>
                    )}
                    {(() => {
                      const savings = calculateSavings(scenario)
                      return savings && savings > 0 ? (
                        <div className="flex justify-between items-center bg-green-100 dark:bg-green-900/30 rounded px-2 py-1">
                          <span className="text-sm font-medium text-green-700 dark:text-green-300">💰 This option saves you:</span>
                          <span className="font-bold text-green-800 dark:text-green-200">
                            {formatCurrency(savings)}
                          </span>
                        </div>
                      ) : null
                    })()}
                    <p className="text-xs text-gray-900 dark:text-gray-600 mt-1">
                      Includes all interest, fees, and debt costs over term
                    </p>
                  </div>
                </div>

                {/* Amortization Results - Less Prominent */}
                <details className="text-xs text-gray-500">
                  <summary className="cursor-pointer hover:text-gray-700 dark:hover:text-gray-300">
                    View full amortization details
                  </summary>
                  <div className="mt-2 space-y-1 pl-4 border-l-2 border-gray-200 dark:border-gray-700">
                    <div className="flex justify-between">
                      <span>Total interest (full amortization):</span>
                      <span>{formatCurrency(scenario.totalInterestOverAmortization || 0)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Total cost (full amortization):</span>
                      <span>{formatCurrency(scenario.totalCostOverAmortization || 0)}</span>
                    </div>
                  </div>
                </details>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Summary Comparison Table */}
      {activeScenarios.length > 1 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <FileText className="h-5 w-5 mr-2" />
            {t('mortgageCalculator.scenarioComparison')}
          </h3>
          
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left py-3 text-gray-600 dark:text-gray-400"></th>
                  {scenarios.filter(s => activeScenarios.includes(s.id)).map(scenario => (
                    <th key={scenario.id} className="text-left py-3 text-gray-900 dark:text-white font-medium">
                      {scenario.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="space-y-2">
                <tr className="border-b border-gray-100 dark:border-gray-700">
                  <td className="py-3 text-gray-600 dark:text-gray-400">{t('mortgageCalculator.propertyValue')}</td>
                  {scenarios.filter(s => activeScenarios.includes(s.id)).map(scenario => (
                    <td key={scenario.id} className="py-3 text-gray-900 dark:text-white">
                      {formatCurrency(scenario.propertyValue)}
                    </td>
                  ))}
                </tr>
                <tr className="border-b border-gray-100 dark:border-gray-700">
                  <td className="py-3 text-gray-600 dark:text-gray-400">Down Payment</td>
                  {scenarios.filter(s => activeScenarios.includes(s.id)).map(scenario => (
                    <td key={scenario.id} className="py-3 text-gray-900 dark:text-white">
                      {formatCurrency(scenario.downPayment)}
                    </td>
                  ))}
                </tr>
                <tr className="border-b border-gray-100 dark:border-gray-700">
                  <td className="py-3 text-gray-600 dark:text-gray-400">Mortgage Amount</td>
                  {scenarios.filter(s => activeScenarios.includes(s.id)).map(scenario => (
                    <td key={scenario.id} className="py-3 text-gray-900 dark:text-white">
                      {formatCurrency(scenario.mortgageAmount)}
                    </td>
                  ))}
                </tr>
                <tr className="border-b border-gray-100 dark:border-gray-700">
                  <td className="py-3 text-gray-600 dark:text-gray-400">Interest Rate</td>
                  {scenarios.filter(s => activeScenarios.includes(s.id)).map(scenario => (
                    <td key={scenario.id} className="py-3 text-gray-900 dark:text-white">
                      {scenario.interestRate}%
                    </td>
                  ))}
                </tr>
                <tr className="border-b border-gray-100 dark:border-gray-700">
                  <td className="py-3 text-gray-600 dark:text-gray-400">Term</td>
                  {scenarios.filter(s => activeScenarios.includes(s.id)).map(scenario => (
                    <td key={scenario.id} className="py-3 text-gray-900 dark:text-white">
                      {scenario.termYears} years {scenario.termMonths > 0 && `${scenario.termMonths} months`}
                    </td>
                  ))}
                </tr>
                <tr className="border-b border-gray-100 dark:border-gray-700">
                  <td className="py-3 text-gray-600 dark:text-gray-400">Amortization</td>
                  {scenarios.filter(s => activeScenarios.includes(s.id)).map(scenario => (
                    <td key={scenario.id} className="py-3 text-gray-900 dark:text-white">
                      {scenario.amortizationYears} years {scenario.amortizationMonths > 0 && `${scenario.amortizationMonths} months`}
                    </td>
                  ))}
                </tr>
                <tr className="border-b border-gray-100 dark:border-gray-700">
                  <td className="py-3 text-gray-600 dark:text-gray-400">Payment Frequency</td>
                  {scenarios.filter(s => activeScenarios.includes(s.id)).map(scenario => (
                    <td key={scenario.id} className="py-3 text-gray-900 dark:text-white">
                      {scenario.paymentFrequency}
                    </td>
                  ))}
                </tr>
                <tr className="border-b border-gray-100 dark:border-gray-700">
                  <td className="py-3 text-gray-600 dark:text-gray-400">Payment</td>
                  {scenarios.filter(s => activeScenarios.includes(s.id)).map(scenario => (
                    <td key={scenario.id} className="py-3 font-semibold text-gray-900 dark:text-white">
                      {formatPayment(scenario.monthlyPayment || 0)}
                    </td>
                  ))}
                </tr>
                <tr className="border-b border-gray-100 dark:border-gray-700">
                  <td className="py-3 text-gray-600 dark:text-gray-400">Debts</td>
                  {scenarios.filter(s => activeScenarios.includes(s.id)).map(scenario => (
                    <td key={scenario.id} className="py-3 text-gray-900 dark:text-white">
                      {scenario.debts.length === 0 ? 'None' : (
                        <div className="text-xs">
                          <div>{scenario.debts.filter(d => d.includeInMortgage).length} consolidated</div>
                          <div>{scenario.debts.filter(d => !d.includeInMortgage).length} separate</div>
                        </div>
                      )}
                    </td>
                  ))}
                </tr>
                {scenarios.some(s => activeScenarios.includes(s.id) && s.savingsFromConsolidation && s.savingsFromConsolidation > 0) && (
                  <tr className="border-b border-gray-100 dark:border-gray-700 bg-green-50 dark:bg-green-900/20">
                    <td className="py-3 text-green-700 dark:text-green-300 font-medium">✅ Consolidation Savings</td>
                    {scenarios.filter(s => activeScenarios.includes(s.id)).map(scenario => (
                      <td key={scenario.id} className="py-3 font-semibold text-green-800 dark:text-green-200">
                        {scenario.savingsFromConsolidation && scenario.savingsFromConsolidation > 0 
                          ? formatCurrency(scenario.savingsFromConsolidation)
                          : '-'
                        }
                      </td>
                    ))}
                  </tr>
                )}
                <tr className="border-b-2 border-blue-200 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20">
                  <td className="py-3 font-semibold text-blue-800 dark:text-blue-200">
                    💰 Total Cost Over Term
                    <div className="text-xs font-normal text-gray-900 dark:text-gray-600">
                      (All costs including debts)
                    </div>
                  </td>
                  {scenarios.filter(s => activeScenarios.includes(s.id)).map(scenario => {
                    const savings = calculateSavings(scenario)
                    return (
                      <td key={scenario.id} className="py-3">
                        <div className="font-bold text-lg text-blue-900 dark:text-blue-100">
                          {formatCurrency(scenario.totalCostOverTerm || 0)}
                        </div>
                        {savings && savings > 0 && (
                          <div className="text-xs text-green-600 dark:text-green-400">
                            Saves {formatCurrency(savings)}
                          </div>
                        )}
                      </td>
                    )
                  })}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Save Scenarios Modal */}
      <SaveScenariosModal
        isOpen={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        onSave={handleSaveScenarios}
        isLoading={saveStatus === 'saving'}
      />

      {/* Saved Scenarios History Panel */}
      {showSavedScenarios ? (
        <div className="mt-8 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden" style={{ minHeight: '400px' }}>
          <div className="bg-gradient-to-r from-gray-900 to-blue-700 px-6 py-4">
            <h2 className="text-xl font-semibold text-white flex items-center">
              <History className="h-6 w-6 mr-3" />
              Saved Scenarios History
            </h2>
            <p className="text-blue-100 text-sm mt-1">
              Browse and download previously saved mortgage comparisons
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 min-h-96">
            {/* Customer Search Panel */}
            <div className="lg:border-r border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
              <div className="p-6">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Select Customer</h3>
                
                {/* Search */}
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search customers..."
                    value={searchCustomerTerm}
                    onChange={(e) => setSearchCustomerTerm(e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                  />
                </div>

                {/* Customer List */}
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {filteredCustomers.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      {customers.length === 0 ? 'Loading customers...' : 'No customers found'}
                    </div>
                  ) : (
                    filteredCustomers.map((customer) => (
                      <div
                        key={customer.id}
                        onClick={() => handleSelectCustomerHistory(customer)}
                        className={`flex items-center p-3 rounded-lg cursor-pointer transition-colors ${
                          selectedCustomerForHistory?.id === customer.id
                            ? 'bg-blue-100 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700'
                            : 'hover:bg-gray-100 dark:hover:bg-gray-800 border border-transparent'
                        }`}
                      >
                        <User className="h-4 w-4 text-gray-400 mr-3 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-900 dark:text-white truncate">
                            {customer.name}
                          </div>
                          {customer.email && (
                            <div className="text-sm text-gray-500 dark:text-gray-400 truncate">
                              {customer.email}
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* Saved Scenarios Panel */}
            <div className="lg:col-span-2 p-6">
              {!selectedCustomerForHistory ? (
                <div className="text-center py-16">
                  <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    Select a Customer
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400">
                    Choose a customer from the list to view their saved mortgage scenarios
                  </p>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {selectedCustomerForHistory.name}'s Scenarios
                      </h3>
                      <p className="text-gray-500 dark:text-gray-400">
                        {savedScenarios.length} saved scenario{savedScenarios.length !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>

                  {savedScenarios.length === 0 ? (
                    <div className="text-center py-12">
                      <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                      <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                        No Saved Scenarios
                      </h4>
                      <p className="text-gray-500 dark:text-gray-400">
                        This customer doesn't have any saved mortgage scenarios yet
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {savedScenarios.map((scenario) => (
                        <div
                          key={scenario.id}
                          className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 hover:border-gray-300 dark:hover:border-gray-500 transition-colors bg-white dark:bg-gray-800"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                                {scenario.title}
                              </h4>
                              <div className="flex items-center text-sm text-gray-500 dark:text-gray-400 mb-2 space-x-4">
                                <div className="flex items-center">
                                  <Calendar className="h-4 w-4 mr-1" />
                                  {formatDate(scenario.created_at)}
                                </div>
                                <div>
                                  {scenario.scenario_count} scenario{scenario.scenario_count !== 1 ? 's' : ''}
                                </div>
                              </div>
                              {scenario.customer_info?.name && (
                                <div className="text-sm text-gray-600 dark:text-gray-300 mb-1">
                                  <strong>{t('mortgageCalculator.customer')}:</strong> {scenario.customer_info.name}
                                </div>
                              )}
                              {scenario.customer_info?.propertyAddress && (
                                <div className="text-sm text-gray-600 dark:text-gray-300">
                                  <strong>{t('mortgageCalculator.property')}:</strong> {scenario.customer_info.propertyAddress}
                                </div>
                              )}
                            </div>

                            <div className="flex items-center space-x-2 ml-4 flex-shrink-0">
                              <PDFDownloadLink
                                document={
                                  <MortgageCalculatorPDF
                                    scenarios={scenario.scenarios_data}
                                    activeScenarios={scenario.scenarios_data.map((s: any) => s.id)}
                                    language={language}
                                    broker={user}
                                    customerName={scenario.customer_info?.name}
                                    propertyAddress={scenario.customer_info?.propertyAddress}
                                  />
                                }
                                fileName={`${scenario.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.pdf`}
                                className="inline-flex items-center px-3 py-2 bg-gray-900 hover:bg-gray-800 text-white text-sm font-medium rounded-md transition-colors"
                              >
                                {({ loading }) => (
                                  <>
                                    <Download className="h-4 w-4 mr-1" />
                                    {loading ? t('mortgageCalculator.generating') : t('mortgageCalculator.download')}
                                  </>
                                )}
                              </PDFDownloadLink>

                              <button
                                onClick={() => handleDeleteScenario(scenario.id)}
                                className="inline-flex items-center px-3 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-md transition-colors"
                                title={t('mortgageCalculator.deleteScenario')}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}

export default MortgageCalculator

